/**
 * DataTable — generic paginated table with optional row-click.
 *
 * A11y notes:
 *   - <table> + <caption> (visually hidden) provides a name for screen readers
 *   - <th scope="col"> wires each column header to its cells
 *   - clickable rows get role="button" + tabIndex + Enter/Space handling so
 *     keyboard users can activate them (not just mouse)
 *   - pagination buttons carry aria-label (icon-only) and the container is
 *     a <nav aria-label="Pagination"> landmark
 *   - loading state advertises aria-busy on the wrapper
 */
import type { KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SkeletonRow } from "@/components/ui/Skeleton";

export interface Column<T> {
  key: string;
  header: string;
  render: (item: T) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  isLoading?: boolean;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  /**
   * Rich empty state that replaces ``emptyMessage`` when the list is
   * empty. Pass the ``<EmptyState>`` component here for list pages that
   * deserve a CTA instead of a bare "no rows" line.
   */
  emptyState?: React.ReactNode;
  /** Accessible caption for the table (visually hidden). */
  caption?: string;
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  isLoading,
  page = 1,
  totalPages = 1,
  onPageChange,
  onRowClick,
  emptyMessage = "No data found",
  emptyState,
  caption = "Data table",
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <div
        aria-busy="true"
        aria-live="polite"
        aria-label="Loading results"
        className="space-y-1"
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  const handleRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>, item: T) => {
    if (!onRowClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onRowClick(item);
    }
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-border">
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={cn(
                    "px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider",
                    col.className
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className={cn(
                    "text-center text-sm text-text-muted",
                    // Rich empty states bring their own padding; the
                    // default string message still wants generous space.
                    emptyState ? "p-0" : "px-4 py-12"
                  )}
                >
                  {emptyState ?? emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr
                  key={keyExtractor(item)}
                  onClick={() => onRowClick?.(item)}
                  onKeyDown={(e) => handleRowKeyDown(e, item)}
                  role={onRowClick ? "button" : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  className={cn(
                    "border-b border-border/50 transition-colors",
                    onRowClick &&
                      "cursor-pointer hover:bg-accent/5 focus:outline-none focus-visible:bg-accent/10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset"
                  )}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn("px-4 py-3 text-sm", col.className)}
                    >
                      {col.render(item)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && onPageChange && (
        <nav
          aria-label="Pagination"
          className="flex items-center justify-between px-4 py-3 border-t border-border"
        >
          <p className="text-sm text-text-muted" aria-live="polite">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </nav>
      )}
    </div>
  );
}
